/**
 * A route served by a remote: the component the router mounts is a
 * `remoteView` over the remote's export, called with the route's context and
 * lifecycle once it loads. On the server the remote is not loaded; the
 * route's loading placeholder is written, marked, and the browser loads the
 * remote into it.
 *
 * A remote that is a registry plugin (`remote.plugin`) is only loaded for a
 * caller the registry gives it to. Withheld - killed, not distributed, not
 * enabled - the route shows its unavailable card with the reason, on both
 * sides; given back, the browser loads it in where the reader is.
 */
import { currentContext } from '@crown/framework/context';
import {
  declareRemote,
  pluginExclusion,
  REMOTE_PENDING_ATTRIBUTE,
  type RemoteProblem,
  remoteServerRendered,
  remoteView,
} from '@crown/framework/federation';
import {
  type Instruction,
  isFunction,
  isInstruction,
  raw,
  safeHtml,
} from '@crown/framework/ir';
import type {
  ComponentDataContext,
  ComponentFunction,
  ComponentLifecycleManager,
  RouteConfig,
} from '../types';
import unavailableCard from './unavailable.tsrx';

export { REMOTE_PENDING_ATTRIBUTE };

/** What the unavailable card's template reads. */
export interface UnavailableCardProps {
  /** The remote the card stands in for; it marks the card for the browser to load the remote into. */
  readonly remote: string;
  readonly message: string;
}

/** The card's template writes `data-c-remote-pending`, which is `REMOTE_PENDING_ATTRIBUTE`. */
const UnavailableCard: (props: UnavailableCardProps) => Instruction =
  unavailableCard;

export function defaultUnavailable(problem: RemoteProblem): Instruction {
  return UnavailableCard({
    remote: problem.remote,
    message: problem.withheld
      ? 'It is switched off here. It will appear as soon as it is switched back on.'
      : problem.reachable
        ? `It answered but could not start: ${problem.reason}`
        : 'It will appear here as soon as it is reachable.',
  });
}

export type RemoteRoute = RouteConfig & {
  remote: NonNullable<RouteConfig['remote']>;
};

/**
 * A remote's exposed module as the router reads it: the export a route names
 * is its page component. Nothing else is read, and that export is checked to
 * be a function before it is called.
 */
type ExposedModule = Readonly<
  Record<string, ComponentFunction<unknown> | undefined>
>;

export function remoteComponent(
  route: RemoteRoute,
  onServer: boolean,
): ComponentFunction<unknown> {
  const { remote } = route;
  const plugin = remote.plugin;
  const component = (
    ctx: ComponentDataContext<unknown>,
    lifecycle: ComponentLifecycleManager,
  ): Instruction => {
    if (!onServer)
      declareRemote({
        name: remote.name,
        entry: remote.entry,
        health: remote.health,
      });
    return remoteView<ExposedModule>({
      remote: remote.name,
      expose: remote.expose,
      server: onServer,
      build(module) {
        remote.prepare?.(module);
        const exported = module[remote.export];
        if (!isFunction(exported))
          throw new Error(`${remote.name} exports no ${remote.export}`);
        const result = exported(ctx, lifecycle);
        if (!isInstruction(result))
          throw new Error(
            `${remote.name}'s ${remote.export} did not return instructions`,
          );
        return result;
      },
      fallback: route.unavailable ?? defaultUnavailable,
      loading: route.loading,
      plugin,
      withheld: plugin ? () => pluginExclusion(plugin) : undefined,
    });
  };
  // SAFETY: a route component is called with a route's context and lifecycle, which is what this takes.
  return component as ComponentFunction<unknown>;
}

/**
 * A remote route for a server render. When the render can ask remotes for
 * their pages (`services.remoteFragment`) and the platform gives this remote
 * to the caller, the remote renders its page for them and it is placed where
 * the route stands: the first HTML carries it, a reader without JavaScript can
 * read it, and the browser adopts it until the remote has loaded. Anything
 * short of that writes the loading placeholder, and the browser loads the
 * remote into it.
 */
export async function serverRemoteComponent(
  route: RemoteRoute,
  params: Readonly<Record<string, string | number | boolean>>,
): Promise<ComponentFunction<unknown>> {
  const { remote } = route;
  const placeholder = remoteComponent(route, true);
  const fragment = currentContext()?.services.remoteFragment;
  if (!fragment || (remote.plugin && pluginExclusion(remote.plugin)))
    return placeholder;
  const html = await fragment({
    remote: remote.name,
    expose: remote.expose,
    export: remote.export,
    params,
  });
  if (html === null) return placeholder;
  // The remote's own renderer wrote this HTML from its templates, escaping every value it placed.
  const page = remoteServerRendered(remote.name, raw(safeHtml(html)));
  const component = (): Instruction => page;
  // SAFETY: a route component is called with a route's context and lifecycle; this one needs neither.
  return component as ComponentFunction<unknown>;
}
